import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { colors, s } from "../lib/theme";

// Clave de API de CARTO para las teselas del mapa — desde agosto de 2026,
// CARTO la exige incluso en su plan gratuito (antes no hacía falta). Se
// consigue gratis en https://carto.com/basemaps/apikey/ y se pone en
// frontend/.env como VITE_CARTO_API_KEY=xxxxx (hay que reconstruir el
// frontend después de añadirla).
const CARTO_API_KEY = import.meta.env.VITE_CARTO_API_KEY || "";

// Un color de borde distinto por banda, para poder distinguirlas en el
// mapa aunque el color de relleno ya esté usado para "país nuevo/trabajado"
// Colores de borde por banda — a propósito solo azules, morados, rosas y
// cian, evitando verdes/ámbares/amarillos porque esos ya significan
// "país nuevo" o "ya trabajado" en el relleno, y se confundían entre sí.
const BAND_COLORS = {
  "160m": "#a78bfa", "80m": "#818cf8", "60m": "#60a5fa", "40m": "#38bdf8",
  "30m": "#22d3ee", "20m": "#f472b6", "17m": "#e879f9", "15m": "#c084fc",
  "12m": "#f0abfc", "10m": "#93c5fd", "6m": "#7dd3fc", "2m": "#a5b4fc",
  "qo-100": "#fda4af",
};

export default function PropagationMap() {
  const { activityId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getPropagation(activityId).then(setData).catch((err) => setError(err.message));
  }, [activityId]);

  const bandsInUse = [...new Set((data?.points || []).map((p) => p.band))];

  return (
    <div style={{ ...s.page, maxWidth: 1200 }}>
      <Link to={`/actividades/${activityId}/matriz`} style={{ display: "inline-flex", gap: 6, color: colors.textDim, fontWeight: 700, fontSize: 14, textDecoration: "none", marginBottom: 16 }}>
        ← Volver a la matriz
      </Link>
      <h1 style={s.h1}>Mapa de propagación</h1>
      <p style={s.subtitle}>
        Quién está recibiendo a {data?.activityCallsign || "la actividad"} ahora mismo, según PSKReporter (últimos 30 min, solo modos digitales).
        {data?.restrictedToBands && ` Mostrando solo tu banda: ${data.restrictedToBands.join(", ")}.`}
      </p>

      {error && <div style={{ ...s.errorBox, marginTop: 16 }}>{error}</div>}

      {data?.stale && (
        <div style={{
          marginTop: 16, padding: "10px 14px", borderRadius: 8,
          background: "#2a2410", border: "1px solid #6b5a1f", color: "#e8d99a", fontSize: 13,
        }}>
          ⚠ PSKReporter no está respondiendo ahora mismo (a veces les pasa, es un servicio gratuito mantenido por una sola persona) —
          mostrando los últimos datos que sí conseguimos, de <b>{formatAge(data.dataAt)}</b>. Se actualizará solo en cuanto vuelva a responder.
        </div>
      )}

      {/* Leyenda fija */}
      <div style={{ ...s.card, marginTop: 16, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 14 }}>
          <LegendDot color={colors.green} label="País nuevo (aún no trabajado)" />
          <LegendDot color={colors.amber} label="País ya trabajado en esta actividad" />
          <LegendDot color={colors.textFaint} label="País no identificado" />
        </div>
        {bandsInUse.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, borderLeft: `1px solid ${colors.lineStrong}`, paddingLeft: 16 }}>
            {bandsInUse.map((band) => (
              <div key={band} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", border: `2.5px solid ${BAND_COLORS[band] || "#888"}`, display: "inline-block" }} />
                <span style={{ fontSize: 12, color: colors.textDim }}>{band}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && (
        <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${colors.line}`, height: 560 }}>
          <MapContainer center={[30, 0]} zoom={2} style={{ height: "100%", width: "100%", background: "#131a22" }}>
            <TileLayer
              // CARTO empezó a exigir clave de API en sus teselas gratuitas
              // (cambio suyo, no nuestro) — gratis hasta 5M peticiones/mes,
              // se consigue al momento en carto.com/basemaps/apikey.
              // Se añade como parámetro "key" en la URL (confirmado en la
              // documentación de CARTO — no "api_key", que era el nombre
              // que había puesto yo por error la primera vez).
              url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png${CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : ""}`}
              attribution='&copy; OpenStreetMap &copy; CARTO'
            />
            {data.points.map((p, i) => (
              <CircleMarker
                key={`${p.receiverCallsign}-${i}`}
                center={[p.lat, p.lon]}
                radius={8}
                pathOptions={{
                  fillColor: p.isNewCountry === null ? colors.textFaint : p.isNewCountry ? colors.green : colors.amber,
                  fillOpacity: 0.85,
                  color: BAND_COLORS[p.band] || "#888",
                  weight: 3,
                }}
              >
                <Tooltip>
                  <b>{p.receiverCallsign}</b><br />
                  {p.country || "País desconocido"}<br />
                  {p.band} · {p.mode}{p.snr != null ? ` · ${p.snr} dB` : ""}
                </Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}

      {data && data.points.length === 0 && (
        <p style={{ color: colors.textFaint, marginTop: 14 }}>
          Todavía no hay reportes de recepción — puede tardar unos minutos en aparecer el primero.
        </p>
      )}
    </div>
  );
}

function formatAge(timestamp) {
  if (!timestamp) return "hace un momento";
  const minutes = Math.round((Date.now() - timestamp) / 60000);
  if (minutes < 1) return "hace menos de un minuto";
  if (minutes === 1) return "hace 1 minuto";
  return `hace ${minutes} minutos`;
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: "50%", background: color, display: "inline-block" }} />
      <span style={{ fontSize: 12, color: colors.textDim }}>{label}</span>
    </div>
  );
}
